import type { DatatableState } from 'components/datatable/datatable.types';
import Cookies from 'js-cookie';
import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import { getLocalStorageObject } from 'utils/localStorage';

/**
 * Temporary message to notify users who previously saved custom table state that we had to reset it
 *
 * CODE CAN BE REMOVED AFTER 2024-10-24
 */

const WARNING_EXPIRE_DATE = '2024-10-24';
const hasWarningExpired = DateTime.now() > DateTime.fromISO(WARNING_EXPIRE_DATE);

const setSeenCookie = (userId: number) => {
  Cookies.set(`seen_timesheet_table_reset_warning_${userId}`, 'true');
};

export const useTableOptionsResetWarning = (userId: number, defaultState: DatatableState) => {
  const [showWarning, setShowWarning] = useState(false);

  const closeOptionsResetWarning = useCallback(() => {
    setSeenCookie(userId);
    setShowWarning(false);
  }, [userId]);

  const tableOptionsResetWarning = useMemo(() => {
    if (hasWarningExpired) {
      return null;
    }

    const oldStorageKey = `USER_${userId}__TIMESHEET_TABLE_STATE`;
    const persistedState = getLocalStorageObject<DatatableState>(oldStorageKey);
    const equal = isEqual(persistedState, defaultState);

    if (persistedState && !equal) {
      if (Cookies.get(`seen_timesheet_table_reset_warning_${userId}`) === 'true') {
        setShowWarning(false);
        return null;
      }
      setShowWarning(true);

      return 'We’ve made some tweaks to the table to improve your experience and reset your table settings. You can now reconfigure it under the options button to suit your needs.';
    }
    return null;
  }, [defaultState, userId]);

  return {
    tableOptionsResetWarning,
    showOptionsResetWarning: showWarning,
    closeOptionsResetWarning,
  };
};
